import { useState, useRef, useEffect } from "react";

function useInView(ref, threshold = 0.12) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVisible(true); },
      { threshold }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [ref, threshold]);
  return visible;
}

function Fade({ children, delay = 0 }) {
  const ref = useRef(null);
  const v = useInView(ref);
  return (
    <div ref={ref} style={{
      opacity: v ? 1 : 0,
      transform: v ? "translateY(0)" : "translateY(24px)",
      transition: `opacity 0.8s ease ${delay}s, transform 0.8s ease ${delay}s`,
    }}>{children}</div>
  );
}

function Accordion({ title, icon, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{
      marginBottom: 8,
      borderRadius: 12,
      border: "1px solid",
      borderColor: open ? "rgba(180,140,100,0.15)" : "rgba(255,255,255,0.04)",
      background: open ? "rgba(180,140,100,0.02)" : "rgba(255,255,255,0.01)",
      overflow: "hidden",
      transition: "all 0.3s ease",
    }}>
      <div onClick={() => setOpen(!open)} style={{
        padding: "16px 20px",
        display: "flex", alignItems: "center", gap: 12,
        cursor: "pointer",
      }}>
        <span style={{ fontSize: 26 }}>{icon}</span>
        <span style={{
          fontFamily: "'Cormorant Garamond', serif",
          fontSize: 26, fontWeight: 400, color: "#d4cdc4",
          flex: 1,
        }}>{title}</span>
        <span style={{
          fontSize: 26, color: "rgba(180,140,100,0.4)",
          transition: "transform 0.3s",
          transform: open ? "rotate(180deg)" : "rotate(0deg)",
        }}>⌄</span>
      </div>
      {open && (
        <div style={{ padding: "0 20px 20px" }}>
          {children}
        </div>
      )}
    </div>
  );
}

export default function LearnPage() {
  const [activeSection, setActiveSection] = useState("guide");

  return (
    <div style={{
      minHeight: "100vh",
      background: "linear-gradient(170deg, #0a0a0c 0%, #0c1018 40%, #0a0f1a 100%)",
      color: "#d4cdc4",
      fontFamily: "'Palatino Linotype', 'Book Antiqua', Palatino, Georgia, serif",
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400&family=Lora:ital,wght@0,400;0,500;1,400&display=swap');
        ::selection { background: rgba(180, 140, 100, 0.3); color: #f0e6d8; }
      `}</style>

      {/* HEADER */}
      <div style={{
        padding: "48px 24px 32px", textAlign: "center",
        position: "relative", overflow: "hidden",
      }}>
        <div style={{
          position: "absolute", inset: 0,
          background: "radial-gradient(ellipse at 50% 40%, rgba(180,140,100,0.03) 0%, transparent 60%)",
        }} />
        <div style={{ position: "relative", maxWidth: 600, margin: "0 auto" }}>
          <div style={{
            fontSize: 17, letterSpacing: "0.35em", textTransform: "uppercase",
            color: "rgba(180,140,100,0.5)", marginBottom: 20,
            fontFamily: "'Lora', serif",
          }}>Learn</div>
          <h1 style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: "clamp(30px, 7vw, 48px)",
            fontWeight: 300, lineHeight: 1.2,
            color: "#e8ddd0", margin: "0 0 16px",
          }}>
            Before You Open <span style={{ fontStyle: "italic", color: "#c4a87c" }}>Any Bottle</span>
          </h1>
          <div style={{ width: 40, height: 1, background: "rgba(180,140,100,0.4)", margin: "0 auto 16px" }} />
          <p style={{
            fontFamily: "'Lora', serif", fontSize: 26, lineHeight: 1.8,
            color: "rgba(200,190,175,0.6)", maxWidth: 440, margin: "0 auto",
          }}>
            How to use this tool, and what you need to understand about supplements 
            to keep your dog safe.
          </p>
        </div>
      </div>

      {/* SECTION TABS */}
      <div style={{
        display: "flex", maxWidth: 620, margin: "0 auto",
        borderBottom: "1px solid rgba(180,140,100,0.08)",
        padding: "0 20px",
      }}>
        {[
          { id: "guide", label: "Using the Tool", icon: "🔍" },
          { id: "education", label: "Supplement Essentials", icon: "📖" },
          { id: "safety", label: "Safety Principles", icon: "⚠️" },
        ].map(t => (
          <button key={t.id} onClick={() => setActiveSection(t.id)} style={{
            flex: 1, padding: "14px 8px", border: "none", cursor: "pointer",
            fontSize: 17, fontFamily: "'Lora', serif", fontWeight: 500,
            background: "transparent",
            color: activeSection === t.id ? "#c4a87c" : "rgba(200,190,175,0.35)",
            borderBottom: activeSection === t.id ? "2px solid #c4a87c" : "2px solid transparent",
            transition: "all 0.2s",
          }}>{t.icon} {t.label}</button>
        ))}
      </div>

      <div style={{ maxWidth: 620, margin: "0 auto", padding: "28px 20px 80px" }}>

        {/* ═══════════════════════════════════════════════ */}
        {/* USING THE TOOL */}
        {/* ═══════════════════════════════════════════════ */}
        {activeSection === "guide" && (
          <>
            <Fade>
              <div style={introStyle}>
                The Supplement Engine is designed to do the cross-referencing for you — the part 
                that takes hours of research when you do it by hand. Here's how to get the 
                most out of it.
              </div>
            </Fade>

            <Fade delay={0.05}>
              <Accordion title="Step 1: Start With Conditions" icon="⚠️" defaultOpen={true}>
                <P>Always add your dog's conditions first, before selecting health goals. This is the 
                safety layer — it tells the engine what to watch out for.</P>
                <P>Select every condition that applies. If your dog has liver disease and diabetes, 
                select both. If they're on anticoagulants, select that too. The more conditions you 
                add, the more precisely the engine can filter out dangerous supplements.</P>
                <Callout type="tip">
                  When in doubt, include a condition. It's better to have a supplement flagged 
                  unnecessarily than to miss a real contraindication.
                </Callout>
              </Accordion>
            </Fade>

            <Fade delay={0.08}>
              <Accordion title="Step 2: Choose Health Goals" icon="🎯">
                <P>After conditions are set, select what you want to support — joints, liver, eyes, 
                calming, immune system, and so on. You can select multiple goals.</P>
                <P>The engine searches its database for every supplement that benefits your selected 
                goals, then cross-references each one against your dog's conditions. The results 
                come back in three groups:</P>
                <ResultExplainer color="#22c55e" label="Safe to Use" emoji="✅"
                  desc="These supplements benefit your selected goals and have no known contraindications with your dog's conditions." />
                <ResultExplainer color="#f59e0b" label="Use With Caution" emoji="⚠️"
                  desc="These may help, but they carry a moderate risk given your dog's conditions. Proceed carefully, ideally with veterinary guidance." />
                <ResultExplainer color="#ef4444" label="Avoid / Contraindicated" emoji="🚫"
                  desc="These are flagged HIGH or CRITICAL risk for your dog's conditions. The engine shows you exactly why — which condition triggers the warning and what the danger is." />
              </Accordion>
            </Fade>

            <Fade delay={0.1}>
              <Accordion title="Step 3: Read the Details" icon="📋">
                <P>Every result card expands when you tap it. Inside you'll find:</P>
                <Detail label="Forms" desc="How the supplement comes — capsule, powder, liquid, tincture. This matters because some forms absorb differently or have different dosing." />
                <Detail label="Dose Range" desc="Typical canine dose. 'Per vet guidance' means the therapeutic window is too narrow for general guidelines." />
                <Detail label="Why It Helps" desc="Each benefit is tagged with an evidence level so you know how well-supported the claim is." />
                <Detail label="Why It's Flagged" desc="For caution and avoid results, you'll see the specific condition that triggered the warning and the reason." />
              </Accordion>
            </Fade>

            <Fade delay={0.12}>
              <Accordion title="Browse Mode" icon="📚">
                <P>The Browse tab lets you explore all 230+ supplements without running a query. 
                You can filter by category — tap "Homeopathic" to see only homeopathic remedies, 
                "TCM Herb" for traditional Chinese medicine, "Flower Essence" for Bach remedies, and so on.</P>
                <P>Each entry in Browse mode shows all known benefits and all known contraindications 
                for that supplement, regardless of any specific dog's conditions. It's your reference library.</P>
              </Accordion>
            </Fade>

            <Fade delay={0.14}>
              <Accordion title="The Recall Tracker" icon="🚨">
                <P>The recall tracker pulls live data from the FDA Recall Enterprise System. It updates 
                weekly on the FDA's end and refreshes every time you load the page.</P>
                <P>Recalls are classified by severity. Class I means serious health consequences 
                or death are possible. Class II means temporary or reversible effects. Class III 
                means unlikely to cause health problems.</P>
                <P>You can filter by severity, by dog vs. cat, and search for specific brands or 
                products. Every recall card expands to show the full details including lot numbers, 
                distribution areas, and the specific reason for the recall.</P>
              </Accordion>
            </Fade>
          </>
        )}

        {/* ═══════════════════════════════════════════════ */}
        {/* SUPPLEMENT EDUCATION */}
        {/* ═══════════════════════════════════════════════ */}
        {activeSection === "education" && (
          <>
            <Fade>
              <div style={introStyle}>
                Not all supplements are created equal. Understanding the categories, 
                how evidence is graded, and what "contraindicated" actually means 
                will help you make better decisions for your dog.
              </div>
            </Fade>

            <Fade delay={0.05}>
              <Accordion title="Supplement Categories Explained" icon="🗂️" defaultOpen={true}>
                <Category name="Conventional Supplements" color="#38bdf8"
                  desc="Vitamins, minerals, amino acids, fatty acids, joint compounds, probiotics, and organ-support 
                  nutrients. These are the most widely studied and commonly recommended by veterinarians. 
                  'Conventional' doesn't mean weak — fish oil, SAMe, milk thistle, and CoQ10 are powerful 
                  bioactive compounds with significant research behind them." />
                <Category name="Homeopathic Remedies" color="#ce93d8"
                  desc="Highly diluted preparations that work on the principle of 'like cures like.' Remedies like 
                  Arnica for trauma, Nux Vomica for digestive upset, and Phosphorus for liver support have been 
                  used in veterinary homeopathy for over a century. Potency matters enormously — 6C, 30C, and 200C 
                  are not interchangeable. Homeopathic remedies are generally very safe when used correctly, but 
                  'aggravations' (temporary worsening) can occur and are actually considered a positive sign in 
                  classical homeopathy." />
                <Category name="TCM Herbs (Traditional Chinese Medicine)" color="#ef5350"
                  desc="Formulas and individual herbs from a 3,000-year medical tradition. TCM views the body through 
                  patterns — Qi stagnation, Blood stasis, Yin deficiency, Damp-Heat — and formulas are built to 
                  address patterns, not just symptoms. Si Miao San for hot swollen joints, Liu Wei Di Huang Wan for 
                  kidney support in seniors, Yunnan Baiyao for bleeding emergencies. These should ideally be prescribed 
                  by a TCVM (Traditional Chinese Veterinary Medicine) practitioner who can identify your dog's pattern." />
                <Category name="Ayurvedic Herbs" color="#ff8a65"
                  desc="From India's ancient medical system. Triphala for gentle detox and digestion, Guduchi as one of 
                  the rare immune modulators safe in autoimmune disease, Brahmi for cognitive support, Ashwagandha 
                  as an adaptogen. Ayurvedic herbs tend to be balancing rather than stimulating, which makes some of 
                  them safer for sensitive dogs — but they still carry contraindications and drug interactions." />
                <Category name="Flower Essences" color="#81c784"
                  desc="Energetic preparations from flowers, most commonly the Bach system. Rescue Remedy for acute 
                  stress, Mimulus for known fears, Star of Bethlehem for grief and trauma. These are among the safest 
                  supplements in the database — no known drug interactions, no toxicity risk, no contraindications. 
                  They work on emotional and behavioral patterns rather than physical biochemistry." />
                <Category name="Essential Oils (Aromatherapy)" color="#ffb74d"
                  desc="Concentrated plant volatile compounds. These are the highest-risk category in this database 
                  for dogs. Many essential oils that are safe for humans are toxic to dogs and especially cats. 
                  Lavender and frankincense are among the safest for canine use, but they must ALWAYS be diluted 
                  for topical use and diffused only in short periods in well-ventilated spaces where the dog can 
                  leave. Never apply undiluted essential oils to a dog. If you have cats in the household, extra 
                  caution is required — cats lack the liver enzyme (glucuronidation) needed to process most essential oils." />
                <Category name="Glandulars" color="#a1887f"
                  desc="Freeze-dried or desiccated organ tissues (liver, thyroid, adrenal, kidney) from bovine or 
                  porcine sources. Based on the 'like supports like' principle — liver glandular contains the 
                  specific nutrients that liver tissue needs. These are used in naturopathic veterinary medicine 
                  but carry real risks, especially thyroid and adrenal glandulars which contain active hormones 
                  and can destabilize existing conditions." />
                <Category name="Whole Food Supplements" color="#90a4ae"
                  desc="Bone broth, goat's milk, organ meats, bee pollen, spirulina, kelp. These are food-based 
                  approaches that deliver nutrients in their whole-food matrix rather than as isolates. Generally 
                  the gentlest category, but not without considerations — kelp can destabilize thyroid function, 
                  manuka honey raises blood sugar in diabetic dogs, and spirulina can be contaminated with 
                  microcystins if sourced poorly." />
              </Accordion>
            </Fade>

            <Fade delay={0.08}>
              <Accordion title="Evidence Levels — What They Mean" icon="🔬">
                <P>Every benefit in the database is tagged with an evidence level. Here's how to read them:</P>
                <EvidenceLevel color="#4caf50" label="Strong"
                  desc="Published peer-reviewed studies in dogs specifically, or well-established veterinary clinical use with broad professional consensus. Examples: fish oil for inflammation, SAMe for liver disease, taurine for cardiac support, turkey tail mushroom for hemangiosarcoma." />
                <EvidenceLevel color="#ffb300" label="Moderate"
                  desc="Some canine studies exist, or strong evidence from human/animal research that's reasonably applicable to dogs. Established use in integrative veterinary practice. Examples: CoQ10 for heart support, quercetin for allergies, phosphatidylserine for cognitive decline." />
                <EvidenceLevel color="#7e57c2" label="Emerging"
                  desc="Early research, preliminary studies, case reports, or in-vitro evidence that hasn't yet been validated in clinical canine trials. Promising but not proven. Examples: PQQ for mitochondrial support, CBG for bladder health, modified citrus pectin for cancer." />
                <EvidenceLevel color="#8d6e63" label="Traditional"
                  desc="Long history of use in traditional medicine systems (TCM, Ayurveda, Western herbalism, homeopathy) without modern clinical trials in dogs. Not 'unproven' — these systems have centuries of observational evidence — but they haven't been through the Western clinical trial process. Examples: most homeopathic remedies, many TCM formulas, flower essences." />
                <Callout type="important">
                  "Traditional" does not mean "weak" or "unscientific." It means the evidence base is observational 
                  and historical rather than double-blind placebo-controlled. Some of the most effective natural 
                  medicines in history were traditional long before anyone studied them in a lab.
                </Callout>
              </Accordion>
            </Fade>

            <Fade delay={0.1}>
              <Accordion title="Understanding Contraindications" icon="🚫">
                <P>A contraindication means a supplement should not be used — or should be used with 
                extreme caution — in the presence of a specific condition. The database uses these risk levels:</P>
                <RiskLevel color="#ff1744" label="CRITICAL"
                  desc="Do not use. Serious potential for harm — organ damage, toxicity, life-threatening interactions. Examples: copper in copper storage disease, methionine in liver disease, butterbur (non-PA-free) in any dog." />
                <RiskLevel color="#ff6d00" label="HIGH"
                  desc="Strong risk. Avoid unless specifically directed by a veterinarian who is aware of the contraindication. Examples: iron in liver disease, glucosamine in diabetes, echinacea in autoimmune disease." />
                <RiskLevel color="#ffd600" label="MODERATE"
                  desc="Real risk that requires monitoring and caution. May be usable under professional guidance with appropriate monitoring. Examples: fish oil with bleeding disorders (monitor, reduce dose), CBD with liver disease (monitor liver enzymes)." />
                <RiskLevel color="#76ff03" label="LOW-MODERATE / CAUTION"
                  desc="Minor or theoretical risk. Worth knowing about, and worth mentioning to your vet, but generally manageable. Examples: melatonin's theoretical effect on insulin sensitivity in diabetic dogs." />
                <Callout type="tip">
                  When you see a contraindication, read the reason. Context matters. 
                  "Fish oil — Pancreatitis — MODERATE" doesn't mean fish oil causes pancreatitis. 
                  It means the fat content could trigger a flare in a pancreatitis-prone dog at high doses, 
                  even though fish oil is actually beneficial long-term at appropriate doses.
                </Callout>
              </Accordion>
            </Fade>

            <Fade delay={0.12}>
              <Accordion title="Drug Interactions" icon="💊">
                <P>Supplements interact with pharmaceutical drugs in several ways:</P>
                <Detail label="Potentiation" desc="The supplement increases the drug's effect. This can be dangerous — for example, fish oil potentiating anticoagulants can cause excessive bleeding." />
                <Detail label="Antagonism" desc="The supplement works against the drug. Echinacea stimulating the immune system while your dog is on immunosuppressants defeats the purpose of the medication." />
                <Detail label="Reduced Absorption" desc="The supplement physically blocks the drug from being absorbed. Slippery elm coats the gut lining and can prevent medications from reaching the bloodstream. Activated charcoal binds virtually everything." />
                <Detail label="CYP450 Inhibition" desc="Many supplements (CBD, milk thistle, goldenseal, schisandra) inhibit liver enzymes that metabolize drugs. This means the drugs stay in the body longer at higher levels than intended — potentially causing toxicity." />
                <Callout type="important">
                  The most common interaction issue is timing. If a supplement can interfere with 
                  drug absorption (like slippery elm or bentonite clay), giving them 2 hours apart 
                  usually solves the problem. Ask your vet about spacing.
                </Callout>
              </Accordion>
            </Fade>
          </>
        )}

        {/* ═══════════════════════════════════════════════ */}
        {/* SAFETY PRINCIPLES */}
        {/* ═══════════════════════════════════════════════ */}
        {activeSection === "safety" && (
          <>
            <Fade>
              <div style={introStyle}>
                These are the principles I learned the hard way — through years of research, 
                trial and error, and watching my own dog react to things I thought would help. 
                Every one of these came at a cost.
              </div>
            </Fade>

            <Fade delay={0.05}>
              <Accordion title="Respect the Dosage" icon="⚖️" defaultOpen={true}>
                <P>This is the first and most important rule. If a homeopathic remedy says one drop, 
                it means one drop. If a supplement says "start low," start low. If it says 
                "per veterinary guidance," that's because the margin between therapeutic and 
                toxic is narrow enough to matter.</P>
                <P>Dogs are not small humans. They metabolize compounds differently. A dose that's 
                fine for a 150-pound person can be dangerous for a 30-pound cocker spaniel. And 
                within dogs, breed differences matter too — some breeds accumulate copper, some 
                are more sensitive to essential oils, some metabolize drugs faster or slower.</P>
                <Callout type="rule">
                  More is not better. More can be dangerous. Start with the lowest recommended 
                  dose and increase only if needed and only if tolerated.
                </Callout>
              </Accordion>
            </Fade>

            <Fade delay={0.08}>
              <Accordion title="Natural Does Not Mean Safe" icon="🌿">
                <P>Arsenic is natural. So is belladonna. So is the death cap mushroom. 
                "Natural" describes the source of a compound, not its safety profile.</P>
                <P>Many of the most dangerous substances on earth are natural. And many of the 
                supplements in this database — alpha lipoic acid, green tea extract, artemisinin, 
                essential oils — can cause serious harm at the wrong dose or in the wrong conditions.</P>
                <P>The question is never "is this natural?" The question is: what does this specific 
                compound do in this specific body, with these specific conditions, at this specific dose?</P>
              </Accordion>
            </Fade>

            <Fade delay={0.1}>
              <Accordion title="One Thing at a Time" icon="🔬">
                <P>When you start a new supplement, start it alone. Don't add three things at 
                once. If your dog has a reaction — good or bad — you need to know which 
                supplement caused it.</P>
                <P>Give each new supplement at least 5-7 days before adding the next one, 
                longer for slow-acting compounds like joint supplements (which can take 4-6 weeks 
                to show effects) or homeopathic constitutional remedies.</P>
                <Callout type="tip">
                  Keep a simple log. Date, supplement, dose, and any changes you notice — 
                  appetite, energy, stool, behavior, skin. Patterns emerge over time that 
                  you won't catch from memory alone.
                </Callout>
              </Accordion>
            </Fade>

            <Fade delay={0.12}>
              <Accordion title="Helping One System Can Hurt Another" icon="⚔️">
                <P>This is the core principle this entire tool was built around. A supplement 
                that supports your dog's joints might stress their liver. Something that boosts 
                the immune system might trigger an autoimmune flare. A fat-soluble vitamin that 
                protects one organ can accumulate to toxic levels in another.</P>
                <P>The body is a system. Every intervention has downstream effects. The Supplement 
                Engine exists specifically to map those downstream effects so you can see the 
                whole picture before you open a bottle.</P>
              </Accordion>
            </Fade>

            <Fade delay={0.14}>
              <Accordion title="Watch for These Warning Signs" icon="👀">
                <P>If your dog shows any of these after starting a new supplement, stop the 
                supplement and contact your vet:</P>
                <Warning text="Vomiting or diarrhea that lasts more than 24 hours" />
                <Warning text="Sudden loss of appetite or refusal to eat" />
                <Warning text="Lethargy, weakness, or unusual sleepiness not explained by sedative supplements" />
                <Warning text="Any change in gum color (pale, yellow, brick red)" />
                <Warning text="Increased thirst or urination (can signal kidney or liver stress)" />
                <Warning text="Bruising, blood in stool, or prolonged bleeding from minor cuts" />
                <Warning text="Seizures, tremors, or sudden behavioral changes" />
                <Warning text="Itching, hives, or swelling (allergic reaction)" />
                <Callout type="rule">
                  When in doubt, stop the supplement. You can always restart it later. 
                  You can't undo harm that's already been done.
                </Callout>
              </Accordion>
            </Fade>

            <Fade delay={0.16}>
              <Accordion title="The Vet Conversation" icon="🩺">
                <P>If your experience with vets has been anything like mine, you may have hit 
                a wall where conventional medicine ran out of answers. That's valid. That's 
                real. And that's exactly the space where this tool lives.</P>
                <P>But even when western medicine has reached its limits, the vet is still your 
                partner — not your opponent. They can run bloodwork that tells you if a supplement 
                is stressing the liver or kidneys. They can monitor drug interactions you might not 
                catch. They can catch things that no supplement database can.</P>
                <P>The best approach: do your research here, build your protocol, and then 
                bring it to your vet — not as a request for permission, but as a conversation 
                between two people who both want your dog to be okay.</P>
                <Callout type="tip">
                  If your vet dismisses natural medicine entirely, find an integrative vet. 
                  The American Holistic Veterinary Medical Association (AHVMA) and the Chi 
                  Institute maintain practitioner directories. You deserve a vet who takes 
                  your research seriously.
                </Callout>
              </Accordion>
            </Fade>

            <Fade delay={0.18}>
              <Accordion title="Quality Matters — A Lot" icon="✨">
                <P>A supplement is only as good as what's actually in the bottle. The pet 
                supplement industry is loosely regulated compared to pharmaceuticals. What's 
                on the label isn't always what's inside.</P>
                <P>Look for supplements that have third-party testing, GMP (Good Manufacturing 
                Practice) certification, or NASC (National Animal Supplement Council) quality 
                seal. For herbs, organic and sustainably sourced matters — kelp from contaminated 
                waters can contain arsenic, and shilajit that hasn't been purified can contain 
                heavy metals.</P>
                <P>For homeopathic remedies, buy from established homeopathic pharmacies. 
                For TCM formulas, buy from companies that supply TCVM veterinarians. 
                For essential oils, therapeutic grade from reputable companies — not 
                fragrance-grade oils from Amazon.</P>
              </Accordion>
            </Fade>
          </>
        )}

        {/* CTA */}
        <Fade delay={0.2}>
          <div style={{
            marginTop: 48, textAlign: "center",
            padding: "32px 24px", borderRadius: 14,
            background: "rgba(180,140,100,0.03)",
            border: "1px solid rgba(180,140,100,0.1)",
          }}>
            <div style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: 26, fontWeight: 300, color: "#d4cdc4", marginBottom: 8,
            }}>
              Ready to find what's safe for your dog?
            </div>
            <div style={{
              fontFamily: "'Lora', serif", fontSize: 17,
              color: "rgba(200,190,175,0.45)", marginBottom: 20, lineHeight: 1.6,
            }}>
              Take what you've learned here and put it to work.
            </div>
            <div style={{
              display: "inline-block", padding: "14px 36px", borderRadius: 8,
              background: "rgba(180,140,100,0.1)",
              border: "1px solid rgba(180,140,100,0.25)",
              color: "#c4a87c", fontFamily: "'Lora', serif",
              fontSize: 26, fontWeight: 500, cursor: "pointer",
            }}>
              Open the Supplement Engine →
            </div>
          </div>
        </Fade>
      </div>

      {/* FOOTER */}
      <div style={{
        padding: "20px", borderTop: "1px solid rgba(180,140,100,0.06)", textAlign: "center",
      }}>
        <div style={{
          fontFamily: "'Lora', serif", fontSize: 17,
          color: "rgba(180,140,100,0.2)", maxWidth: 500, margin: "0 auto", lineHeight: 1.6,
        }}>
          This guide is for educational purposes. Always consult with a veterinarian — 
          ideally one who respects both conventional and integrative approaches — 
          before starting any supplement protocol.
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// SUB-COMPONENTS
// ═══════════════════════════════════════════════════════════════

function P({ children }) {
  return (
    <p style={{
      fontFamily: "'Lora', serif", fontSize: 26, lineHeight: 1.85,
      color: "rgba(200,190,175,0.7)", margin: "0 0 14px",
    }}>{children}</p>
  );
}

function Callout({ type, children }) {
  const colors = {
    tip: { bg: "rgba(56,189,248,0.04)", border: "rgba(56,189,248,0.15)", icon: "💡", accent: "#7dd3fc" },
    important: { bg: "rgba(180,140,100,0.04)", border: "rgba(180,140,100,0.15)", icon: "📌", accent: "#c4a87c" },
    rule: { bg: "rgba(239,68,68,0.04)", border: "rgba(239,68,68,0.12)", icon: "⚡", accent: "#fca5a5" },
  };
  const c = colors[type] || colors.tip;
  return (
    <div style={{
      padding: "14px 16px", borderRadius: 10, margin: "14px 0",
      background: c.bg, border: `1px solid ${c.border}`,
      display: "flex", gap: 10, alignItems: "flex-start",
    }}>
      <span style={{ fontSize: 26, flexShrink: 0 }}>{c.icon}</span>
      <div style={{
        fontFamily: "'Lora', serif", fontSize: 17, lineHeight: 1.7,
        color: c.accent,
      }}>{children}</div>
    </div>
  );
}

function ResultExplainer({ color, label, emoji, desc }) {
  return (
    <div style={{
      display: "flex", gap: 12, alignItems: "flex-start",
      padding: "12px 14px", borderRadius: 8, margin: "8px 0",
      background: `${color}08`, borderLeft: `3px solid ${color}40`,
    }}>
      <span style={{ fontSize: 26, flexShrink: 0 }}>{emoji}</span>
      <div>
        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 17, fontWeight: 600, color, marginBottom: 2 }}>
          {label}
        </div>
        <div style={{ fontFamily: "'Lora', serif", fontSize: 26, color: "rgba(200,190,175,0.55)", lineHeight: 1.6 }}>
          {desc}
        </div>
      </div>
    </div>
  );
}

function Category({ name, color, desc }) {
  return (
    <div style={{
      padding: "14px 16px", borderRadius: 10, margin: "10px 0",
      background: `${color}08`, border: `1px solid ${color}15`,
    }}>
      <div style={{
        fontFamily: "'Cormorant Garamond', serif",
        fontSize: 26, fontWeight: 600, color, marginBottom: 6,
      }}>{name}</div>
      <div style={{
        fontFamily: "'Lora', serif", fontSize: 17,
        color: "rgba(200,190,175,0.6)", lineHeight: 1.7,
      }}>{desc}</div>
    </div>
  );
}

function EvidenceLevel({ color, label, desc }) {
  return (
    <div style={{
      display: "flex", gap: 12, alignItems: "flex-start",
      padding: "12px 14px", borderRadius: 8, margin: "8px 0",
      background: "rgba(255,255,255,0.015)",
      borderLeft: `3px solid ${color}`,
    }}>
      <div style={{
        padding: "2px 10px", borderRadius: 10,
        background: `${color}18`, color,
        fontSize: 17, fontWeight: 700, fontFamily: "'Lora', serif",
        flexShrink: 0, whiteSpace: "nowrap",
      }}>{label}</div>
      <div style={{
        fontFamily: "'Lora', serif", fontSize: 17,
        color: "rgba(200,190,175,0.55)", lineHeight: 1.6,
      }}>{desc}</div>
    </div>
  );
}

function RiskLevel({ color, label, desc }) {
  return (
    <div style={{
      padding: "12px 14px", borderRadius: 8, margin: "8px 0",
      background: `${color}08`, borderLeft: `3px solid ${color}50`,
    }}>
      <div style={{
        fontSize: 17, fontWeight: 700, color,
        fontFamily: "'Lora', serif", marginBottom: 4,
      }}>{label}</div>
      <div style={{
        fontFamily: "'Lora', serif", fontSize: 17,
        color: "rgba(200,190,175,0.55)", lineHeight: 1.6,
      }}>{desc}</div>
    </div>
  );
}

function Detail({ label, desc }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <span style={{
        fontFamily: "'Lora', serif", fontSize: 17,
        fontWeight: 600, color: "#c4a87c",
      }}>{label}: </span>
      <span style={{
        fontFamily: "'Lora', serif", fontSize: 17,
        color: "rgba(200,190,175,0.6)", lineHeight: 1.6,
      }}>{desc}</span>
    </div>
  );
}

function Warning({ text }) {
  return (
    <div style={{
      display: "flex", gap: 8, alignItems: "center",
      padding: "8px 12px", margin: "4px 0",
      borderRadius: 6, background: "rgba(239,68,68,0.04)",
    }}>
      <span style={{ color: "#ef4444", fontSize: 26 }}>●</span>
      <span style={{
        fontFamily: "'Lora', serif", fontSize: 17,
        color: "rgba(200,190,175,0.6)",
      }}>{text}</span>
    </div>
  );
}

const introStyle = {
  fontFamily: "'Lora', serif",
  fontSize: 17, lineHeight: 1.85,
  color: "rgba(200,190,175,0.65)",
  marginBottom: 24,
  fontStyle: "italic",
  textAlign: "center",
  maxWidth: 480,
  margin: "0 auto 24px",
};
